import React, { useState, useEffect, createContext, useContext } from 'react';
import { 
    List, 
    Datagrid, 
    TextField, 
    FunctionField,
    useListContext,
    useDataProvider,
    TopToolbar,
    Edit,
    SimpleForm,
    TextInput,
    useRecordContext,
    Toolbar,
    DeleteButton,
    useNotify,
    useRedirect,
    FormDataConsumer,
    CreateButton
} from 'react-admin';
import { useFormState, useFormContext } from 'react-hook-form';
import { Autocomplete, TextField as MuiTextField, Box, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button, ButtonBase, Select, MenuItem, FormControl, InputLabel, InputAdornment, Chip, Tooltip, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PetsIcon from '@mui/icons-material/Pets';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import { uploadListIcon, uploadMapIcon, uploadGalleryImage, deleteListIcon, deleteGalleryImage, uploadAnimalAudio, deleteAnimalAudio } from './s3Service';
import YandexMapPicker from './YandexMapPicker';
import DetailLoadingState from './components/DetailLoadingState';

// Контекст для передачи данных изображений между компонентами
const AnimalImagesContext = createContext({
    images: [],
    setImages: () => {},
    mapIconFile: null,
    setMapIconFile: () => {},
    listIconFile: null,
    setListIconFile: () => {},
    deleteMapIcon: false,
    setDeleteMapIcon: () => {},
    deleteListIcon: false,
    setDeleteListIcon: () => {},
    mapIconUrl: null,
    setMapIconUrl: () => {},
    listIconUrl: null,
    setListIconUrl: () => {},
    audioFile: null,
    setAudioFile: () => {},
    audioUrl: null,
    setAudioUrl: () => {},
    deleteAudio: false,
    setDeleteAudio: () => {}
});

const PLACEHOLDER_IMAGE_URL = 'http://example.com';

const isValidImageUrl = (url) =>
    url && url !== PLACEHOLDER_IMAGE_URL && !url.startsWith('http://example.com');

const getMainImageUrl = (record) => {
    if (!record?.images?.length) return null;
    const first = record.images[0];
    return typeof first === 'string' ? first : first?.url;
};

const AnimalImageCell = ({ url, alt, size = 52, rounded = false }) => {
    if (!isValidImageUrl(url)) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                —
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 0.5,
            }}
        >
            <Box
                component="img"
                src={url}
                alt={alt}
                sx={{
                    width: size,
                    height: size,
                    objectFit: 'contain',
                    borderRadius: rounded ? '50%' : 1,
                    bgcolor: 'action.hover',
                }}
            />
        </Box>
    );
};

const AnimalMediaCell = ({ record }) => {
    const media = [
        { url: getMainImageUrl(record), label: 'Основное фото' },
        { url: record.list_icon_url, label: 'Иконка списка' },
        { url: record.map_icon_url, label: 'Иконка карты', rounded: true },
    ].filter((item) => isValidImageUrl(item.url));

    if (!media.length) {
        return (
            <Box className="animals-media-empty" aria-label="Изображения не добавлены">
                <PetsIcon fontSize="small" />
            </Box>
        );
    }

    return (
        <Box className="animals-media-stack" aria-label={`Добавлено изображений: ${media.length}`}>
            {media.map((item, index) => (
                <Tooltip title={item.label} key={`${item.label}-${item.url}`}>
                    <Box
                        component="img"
                        src={item.url}
                        alt={item.label}
                        loading="lazy"
                        sx={{
                            zIndex: media.length - index,
                            borderRadius: item.rounded ? '50%' : 1.5,
                        }}
                    />
                </Tooltip>
            ))}
        </Box>
    );
};

const AnimalTaxonomy = ({ record, compact = false }) => (
    <Box className={`animals-taxonomy${compact ? ' animals-taxonomy-compact' : ''}`}>
        <Typography variant="body2" className="animals-taxonomy-primary">
            {record.family?.name || 'Семейство не указано'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
            {[record.family?.order?.class_?.name, record.family?.order?.name].filter(Boolean).join(' · ') || 'Классификация не указана'}
        </Typography>
    </Box>
);

const AnimalDatagrid = () => {
    const { data } = useListContext();
    
    return (
        <Datagrid
            data={data}
            bulkActionButtons={false}
            rowClick="edit"
            sx={{
                '& .RaDatagrid-row': { transition: 'background-color 160ms ease' },
                '& .column-id': { width: 88 },
                '& .column-images': { width: 126 },
                '& .column-name': { minWidth: 210 },
                '& .column-description': { minWidth: 260, maxWidth: 420 },
                '& .column-actions': { width: 52, px: 1 },
            }}
        >
            <FunctionField
                source="id"
                label="ID"
                render={(record) => <Typography className="animals-id">#{record.id}</Typography>}
            />
            <FunctionField
                source="images"
                label="Медиа"
                render={(record) => <AnimalMediaCell record={record} />}
            />
            <FunctionField
                source="name"
                label="Животное"
                render={(record) => (
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{record.name || 'Без названия'}</Typography>
                        <Typography variant="caption" color="text.secondary">Карточка животного</Typography>
                    </Box>
                )}
            />
            <FunctionField
                source="family"
                label="Классификация"
                render={(record) => <AnimalTaxonomy record={record} />}
            />
            <FunctionField
                source="description"
                label="Описание"
                render={(record) => {
                    const text = record.description || '';
                    return <Typography variant="body2" color="text.secondary" className="animals-description">{text || 'Описание пока не добавлено'}</Typography>;
                }}
            />
            <FunctionField
                source="actions"
                label=""
                render={() => <ChevronRightIcon className="animals-row-arrow" aria-hidden="true" />}
            />
        </Datagrid>
    );
};

const AnimalMobileList = () => {
    const { data = [] } = useListContext();
    const redirect = useRedirect();

    return (
        <Box className="animals-mobile-list">
            {data.map((record) => (
                <ButtonBase key={record.id} className="animals-mobile-card" onClick={() => redirect('edit', 'animals', record.id)}>
                    <Box className="animals-mobile-thumb">
                        {isValidImageUrl(getMainImageUrl(record) || record.list_icon_url) ? (
                            <Box component="img" src={getMainImageUrl(record) || record.list_icon_url} alt="" loading="lazy" />
                        ) : <PetsIcon />}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                        <Typography variant="caption" color="text.secondary" className="animals-id">#{record.id}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }} noWrap>{record.name || 'Без названия'}</Typography>
                        <AnimalTaxonomy record={record} compact />
                    </Box>
                    <ChevronRightIcon color="action" />
                </ButtonBase>
            ))}
        </Box>
    );
};

// Кастомный компонент фильтра для выбора с каскадной фильтрацией
const CustomFilterInput = ({ source, label, allData }) => {
    const [choices, setChoices] = useState([]);
    const [value, setValue] = useState(null);
    const { setFilters, filterValues } = useListContext();

    // Обновляем choices при изменении фильтров
    useEffect(() => {
        if (allData.length === 0) return;

        // Фильтруем данные на основе текущих фильтров
        let filteredData = allData.filter(item => {
            // Применяем все фильтры кроме текущего
            if (filterValues['family.order.class_.name'] && source !== 'family.order.class_.name') {
                if (item.family?.order?.class_?.name !== filterValues['family.order.class_.name']) {
                    return false;
                }
            }
            if (filterValues['family.order.name'] && source !== 'family.order.name') {
                if (item.family?.order?.name !== filterValues['family.order.name']) {
                    return false;
                }
            }
            if (filterValues['family.name'] && source !== 'family.name') {
                if (item.family?.name !== filterValues['family.name']) {
                    return false;
                }
            }
            return true;
        });

        // Извлекаем уникальные значения из отфильтрованных данных
        const uniqueValues = new Set();
        filteredData.forEach(item => {
            let val;
            if (source === 'family.name') {
                val = item.family?.name;
            } else if (source === 'family.order.name') {
                val = item.family?.order?.name;
            } else if (source === 'family.order.class_.name') {
                val = item.family?.order?.class_?.name;
            }
            
            if (val) {
                uniqueValues.add(val);
            }
        });

        const choicesArray = Array.from(uniqueValues).sort();
        setChoices(choicesArray);
    }, [allData, filterValues, source]);

    // Синхронизируем значение с filterValues
    useEffect(() => {
        if (filterValues[source]) {
            setValue(filterValues[source]);
        } else {
            setValue(null);
        }
    }, [filterValues, source]);

    const handleChange = (event, newValue) => {
        setValue(newValue);
        
        if (newValue) {
            // При изменении фильтра сбрасываем зависимые фильтры
            const newFilters = { ...filterValues, [source]: newValue };
            
            // Если изменили класс, сбрасываем отряд и семейство
            if (source === 'family.order.class_.name') {
                delete newFilters['family.order.name'];
                delete newFilters['family.name'];
            }
            // Если изменили отряд, сбрасываем семейство
            else if (source === 'family.order.name') {
                delete newFilters['family.name'];
            }
            
            setFilters(newFilters);
        } else {
            const newFilters = { ...filterValues };
            delete newFilters[source];
            
            // При сбросе также сбрасываем зависимые фильтры
            if (source === 'family.order.class_.name') {
                delete newFilters['family.order.name'];
                delete newFilters['family.name'];
            } else if (source === 'family.order.name') {
                delete newFilters['family.name'];
            }
            
            setFilters(newFilters);
        }
    };

    return (
        <Autocomplete
            value={value}
            onChange={handleChange}
            options={choices}
            renderInput={(params) => (
                <MuiTextField 
                    {...params} 
                    label={label}
                    variant="outlined"
                    size="small"
                />
            )}
            sx={{ minWidth: { xs: '100%', sm: 180 }, flex: '1 1 180px' }}
        />
    );
};

// Кастомный компонент для текстового поиска
const SearchInput = () => {
    const { setFilters, filterValues } = useListContext();
    const [value, setValue] = useState('');

    useEffect(() => {
        if (filterValues.name) {
            setValue(filterValues.name);
        } else {
            setValue('');
        }
    }, [filterValues.name]);

    const handleChange = (event) => {
        const newValue = event.target.value;
        setValue(newValue);
        
        if (newValue) {
            setFilters({ ...filterValues, name: newValue });
        } else {
            const newFilters = { ...filterValues };
            delete newFilters.name;
            setFilters(newFilters);
        }
    };

    return (
        <MuiTextField
            label="Поиск по имени"
            variant="outlined"
            size="small"
            value={value}
            onChange={handleChange}
            placeholder="Например, манул"
            InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
            sx={{ minWidth: { xs: '100%', md: 280 }, flex: '2 1 280px' }}
        />
    );
};

// Кастомная панель фильтров
const AnimalFilters = () => {
    const dataProvider = useDataProvider();
    const { filterValues, setFilters } = useListContext();
    const [allData, setAllData] = useState([]);
    const activeCount = Object.values(filterValues).filter(Boolean).length;

    useEffect(() => {
        let active = true;
        dataProvider.getList('animals', {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'id', order: 'ASC' },
            filter: {},
        }).then(({ data }) => {
            if (active) setAllData(data);
        }).catch((error) => console.error('Error loading animal filters:', error));
        return () => { active = false; };
    }, [dataProvider]);

    return (
        <Box className="animals-filter-panel">
            <Box className="animals-filter-heading">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterAltOutlinedIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2">Поиск и фильтры</Typography>
                    {activeCount > 0 && <Chip size="small" color="primary" label={activeCount} />}
                </Box>
                <Button size="small" startIcon={<ClearAllIcon />} disabled={!activeCount} onClick={() => setFilters({})}>Сбросить</Button>
            </Box>
            <Box className="animals-filter-fields">
                <SearchInput />
                <CustomFilterInput source="family.order.class_.name" label="Класс" allData={allData} />
                <CustomFilterInput source="family.order.name" label="Отряд" allData={allData} />
                <CustomFilterInput source="family.name" label="Семейство" allData={allData} />
            </Box>
        </Box>
    );
};

// Пустой компонент actions чтобы убрать стандартные кнопки
const ListActions = () => <TopToolbar />;

// Кастомные actions для страницы редактирования
const AnimalEditActions = () => {
    const redirect = useRedirect();

    return (
        <TopToolbar className="animal-editor-actions">
            <Button startIcon={<ArrowBackIcon />} onClick={() => redirect('list', 'animals')}>
                Все животные
            </Button>
        </TopToolbar>
    );
};

const AnimalEditorSectionTitle = ({ icon, title, description, action }) => (
    <Box className="animal-editor-section-title">
        <Box className="animal-editor-section-icon" aria-hidden="true">{icon}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography component="h2" className="animal-editor-section-heading">{title}</Typography>
            {description && <Typography className="animal-editor-section-description">{description}</Typography>}
        </Box>
        {action}
    </Box>
);

const AnimalMediaEmpty = ({ label }) => (
    <Box className="animal-editor-media-empty">
        <ImageOutlinedIcon />
        <Typography>{label}</Typography>
    </Box>
);

// Кастомная панель инструментов для редактирования
const AnimalEditToolbar = props => {
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const { isDirty } = useFormState();
    const { getValues } = useFormContext();
    const { 
        images, 
        deleteMapIcon, 
        deleteListIcon,
        mapIconUrl,
        listIconUrl,
        audioUrl,
        deleteAudio
    } = useContext(AnimalImagesContext);
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();
    const record = useRecordContext();

    const handleSaveClick = () => {
        setSaveDialogOpen(true);
    };

    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
    };

    const handleSaveConfirm = async () => {
        setSaveDialogOpen(false);
        
        try {
            // Получаем данные из react-hook-form
            const formValues = getValues();
            
            // Преобразуем geolocation в массив чисел
            const geolocation = [
                parseFloat(formValues.geolocation?.[0]) || 0,
                parseFloat(formValues.geolocation?.[1]) || 0
            ];
            
            // Собираем параметры
            const parameters = [];
            if (formValues.parameters && Array.isArray(formValues.parameters)) {
                formValues.parameters.forEach((param, index) => {
                    if (param?.key && param?.value) {
                        parameters.push({
                            animal_id: record.id,
                            key: param.key,
                            value: param.value
                        });
                    }
                });
            }
            
            // Подготавливаем данные для обновления животного
            const animalData = {
                name: formValues.name,
                family_id: record.family?.id,
                description: formValues.description || '',
                environment_description: formValues.environment_description || '',
                zoo_description: formValues.zoo_description || '',
                geolocation: geolocation,
                qr_url: formValues.qr_url || 'http://example.com',
                map_icon_url: deleteMapIcon ? '' : (mapIconUrl || formValues.map_icon_url || 'http://example.com'),
                list_icon_url: deleteListIcon ? '' : (listIconUrl || formValues.list_icon_url || 'http://example.com'),
                audio_url: deleteAudio ? 'http://example.com' : (audioUrl || formValues.audio_url || 'http://example.com'),
                images: images || []
            };
            
            // Обновляем животное
            await dataProvider.update('animals', {
                id: record.id,
                data: animalData,
                previousData: record
            });
            
            // Удаляем старые параметры
            if (record.parameters && record.parameters.length > 0) {
                await Promise.all(
                    record.parameters.map(param => 
                        dataProvider.delete('animals/parameters', {
                            id: param.id,
                            previousData: param
                        })
                    )
                );
            }
            
            // Добавляем новые параметры
            if (parameters.length > 0) {
                await Promise.all(
                    parameters.map(param =>
                        dataProvider.create('animals/parameters', {
                            data: param
                        })
                    )
                );
            }
            
            notify('Животное успешно обновлено', { type: 'success' });
            redirect('list', 'animals');
            
        } catch (error) {
            console.error('Error saving animal:', error);
            // Извлекаем детальное сообщение об ошибке
            let errorMessage = 'Неизвестная ошибка';
            if (error.message) {
                errorMessage = error.message;
            }
            notify(`Ошибка при сохранении: ${errorMessage}`, { type: 'error' });
        }
    };

    return (
        <>
            <Toolbar {...props} className="animal-editor-toolbar">
                <Box className="animal-editor-toolbar-inner">
                    <Box className={`animal-editor-change-status${isDirty ? ' is-dirty' : ''}`}>
                        <CheckCircleOutlineIcon />
                        <Typography component="span">
                            {isDirty ? 'Есть несохранённые изменения' : 'Все изменения сохранены'}
                        </Typography>
                    </Box>
                    <Box className="animal-editor-toolbar-actions">
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleDeleteClick}
                            startIcon={<DeleteOutlineIcon />}
                        >
                            Удалить
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveClick}
                            disabled={!isDirty}
                        >
                            Сохранить изменения
                        </Button>
                    </Box>
                </Box>
            </Toolbar>

            {/* Диалог подтверждения сохранения */}
            <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
                <DialogTitle>Подтверждение сохранения</DialogTitle>
                <DialogContent>
                    <Typography>
                        Вы уверены, что хотите сохранить изменения?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSaveDialogOpen(false)}>
                        Отмена
                    </Button>
                    <Button onClick={handleSaveConfirm} variant="contained" autoFocus>
                        Сохранить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог подтверждения удаления */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Подтверждение удаления</DialogTitle>
                <DialogContent>
                    <Typography>
                        Вы уверены, что хотите удалить это животное? Это действие нельзя отменить.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Отмена
                    </Button>
                    <DeleteButton 
                        label="Удалить"
                        confirmTitle=""
                        mutationMode="pessimistic"
                        onClick={() => setDeleteDialogOpen(false)}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};

// Компонент редактирования животного
export const AnimalEdit = () => {
    const [images, setImages] = useState([]);
    const [mapIconFile, setMapIconFile] = useState(null);
    const [listIconFile, setListIconFile] = useState(null);
    const [deleteMapIcon, setDeleteMapIcon] = useState(false);
    const [shouldDeleteListIcon, setShouldDeleteListIcon] = useState(false);
    const [mapIconUrl, setMapIconUrl] = useState(null);
    const [listIconUrl, setListIconUrl] = useState(null);
    const [audioFile, setAudioFile] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [deleteAudio, setDeleteAudio] = useState(false);
    
    return (
        <AnimalImagesContext.Provider value={{ 
            images, 
            setImages, 
            mapIconFile, 
            setMapIconFile,
            listIconFile,
            setListIconFile,
            deleteMapIcon,
            setDeleteMapIcon,
            deleteListIcon: shouldDeleteListIcon,
            setDeleteListIcon: setShouldDeleteListIcon,
            mapIconUrl,
            setMapIconUrl,
            listIconUrl,
            setListIconUrl,
            audioFile,
            setAudioFile,
            audioUrl,
            setAudioUrl,
            deleteAudio,
            setDeleteAudio
        }}>
            <Edit className="animal-editor" title="Редактирование животного" actions={<AnimalEditActions />}>
                <SimpleForm className="animal-editor-form" toolbar={<AnimalEditToolbar />}>
                    <AnimalEditContent />
                </SimpleForm>
            </Edit>
        </AnimalImagesContext.Provider>
    );
};

// Контент формы редактирования
const AnimalEditContent = () => {
    const record = useRecordContext();
    const { 
        images, 
        setImages, 
        mapIconFile, 
        setMapIconFile, 
        listIconFile, 
        setListIconFile,
        deleteMapIcon,
        setDeleteMapIcon,
        deleteListIcon: shouldDeleteListIconFlag,
        setDeleteListIcon: setShouldDeleteListIcon,
        mapIconUrl,
        setMapIconUrl,
        listIconUrl,
        setListIconUrl,
        audioFile,
        setAudioFile,
        audioUrl,
        setAudioUrl,
        deleteAudio: shouldDeleteAudioFlag,
        setDeleteAudio
    } = useContext(AnimalImagesContext);
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const { setValue } = useFormContext();
    const [uploading, setUploading] = useState(false);
    
    // Инициализируем изображения из record
    useEffect(() => {
        if (record && record.images) {
            setImages(record.images);
        }
        if (record && record.list_icon_url) {
            setListIconUrl(record.list_icon_url);
        }
        if (record && record.map_icon_url) {
            setMapIconUrl(record.map_icon_url);
        }
        if (record && record.audio_url) {
            setAudioUrl(record.audio_url);
        }
    }, [record, setImages, setListIconUrl, setMapIconUrl, setAudioUrl]);
    
    const handleMapIconChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        if (!record || !record.id) {
            notify('Ошибка: ID животного не найден', { type: 'error' });
            return;
        }
        
        setUploading(true);
        
        try {
            const url = await uploadMapIcon(file, record.id);
            setMapIconUrl(url);
            setMapIconFile(file);
            setDeleteMapIcon(false);
            
            // Обновляем данные животного сразу после загрузки
            await dataProvider.update('animals', {
                id: record.id,
                data: {
                    ...record,
                    map_icon_url: url
                },
                previousData: record
            });
            
            notify('Иконка на карте успешно загружена', { type: 'success' });
        } catch (error) {
            console.error('Error uploading map icon:', error);
            notify(`Ошибка загрузки: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };
    
    const handleDeleteMapIcon = () => {
        setMapIconFile(null);
        setDeleteMapIcon(true);
        setMapIconUrl('http://example.com');
        notify('Иконка на карте будет удалена при сохранении', { type: 'info' });
    };
    
    const handleListIconChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        if (!record || !record.id) {
            notify('Ошибка: ID животного не найден', { type: 'error' });
            return;
        }
        
        setUploading(true);
        
        try {
            // Загружаем файл на S3
            const url = await uploadListIcon(file, record.id);
            setListIconUrl(url);
            setListIconFile(file);
            setShouldDeleteListIcon(false);
            
            // Обновляем данные животного сразу после загрузки
            await dataProvider.update('animals', {
                id: record.id,
                data: {
                    ...record,
                    list_icon_url: url
                },
                previousData: record
            });
            
            notify(`Иконка в списке успешно загружена`, { type: 'success' });
        } catch (error) {
            console.error('Error uploading list icon:', error);
            notify(`Ошибка загрузки: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };
    
    const handleDeleteListIcon = async () => {
        console.log('=== handleDeleteListIcon START ===');
        
        if (!record || !record.id) {
            console.error('No record or record.id');
            notify('Ошибка: ID животного не найден', { type: 'error' });
            return;
        }
        
        console.log('Record ID:', record.id);
        console.log('Current listIconUrl:', listIconUrl);
        
        setUploading(true);
        
        try {
            console.log('Step 1: Updating animal in database...');
            
            // Сначала обновляем данные животного (устанавливаем list_icon_url в http://example.com/)
            await dataProvider.update('animals', {
                id: record.id,
                data: {
                    name: record.name,
                    family_id: record.family?.id,
                    description: record.description || '',
                    environment_description: record.environment_description || '',
                    zoo_description: record.zoo_description || '',
                    geolocation: record.geolocation || [0, 0],
                    qr_url: record.qr_url || 'http://example.com',
                    map_icon_url: record.map_icon_url || 'http://example.com',
                    list_icon_url: 'http://example.com',
                    audio_url: record.audio_url || 'http://example.com'
                },
                previousData: record
            });
            
            console.log('Step 1: Database updated successfully');
            console.log('Step 2: Deleting file from S3...');
            console.log('Calling deleteListIcon with animalId:', record.id);
            
            // Затем удаляем файл из S3
            await deleteListIcon(record.id);
            
            console.log('Step 2: File deleted from S3 successfully');
            
            setListIconFile(null);
            setListIconUrl('http://example.com');
            setShouldDeleteListIcon(true);
            
            console.log('=== handleDeleteListIcon SUCCESS ===');
            notify('Иконка в списке удалена', { type: 'success' });
        } catch (error) {
            console.error('=== handleDeleteListIcon ERROR ===');
            console.error('Error details:', error);
            console.error('Error message:', error.message);
            console.error('Error stack:', error.stack);
            notify(`Ошибка удаления: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleAudioChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!record || !record.id) {
            notify('Ошибка: ID животного не найден', { type: 'error' });
            return;
        }

        setUploading(true);

        try {
            const url = await uploadAnimalAudio(file, record.id);
            setAudioUrl(url);
            setAudioFile(file);
            setDeleteAudio(false);
            setValue('audio_url', url, { shouldDirty: true, shouldTouch: true });

            await dataProvider.update('animals', {
                id: record.id,
                data: {
                    ...record,
                    audio_url: url
                },
                previousData: record
            });

            notify('Аудио успешно загружено', { type: 'success' });
        } catch (error) {
            console.error('Error uploading audio:', error);
            notify(`Ошибка загрузки аудио: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteAudio = async () => {
        if (!record || !record.id) {
            notify('Ошибка: ID животного не найден', { type: 'error' });
            return;
        }

        setUploading(true);

        try {
            await dataProvider.update('animals', {
                id: record.id,
                data: {
                    ...record,
                    audio_url: 'http://example.com'
                },
                previousData: record
            });

            await deleteAnimalAudio(record.id);

            setAudioFile(null);
            setAudioUrl('http://example.com');
            setDeleteAudio(true);
            setValue('audio_url', 'http://example.com', { shouldDirty: true, shouldTouch: true });

            notify('Аудио удалено', { type: 'success' });
        } catch (error) {
            console.error('Error deleting audio:', error);
            notify(`Ошибка удаления аудио: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };
    
    const handleGalleryImagesChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        
        if (!record || !record.id) {
            notify('Ошибка: ID животного не найден', { type: 'error' });
            return;
        }
        
        setUploading(true);
        
        try {
            const currentImages = [...images];
            
            console.log('=== DEBUG: Starting upload ===');
            console.log('Files to upload:', files.length);
            console.log('Current images count:', currentImages.length);
            console.log('Current images:', currentImages);
            
            // Находим максимальный индекс из существующих изображений
            let maxIndex = -1;
            currentImages.forEach(image => {
                const url = typeof image === 'string' ? image : image.url;
                // Парсим URL: main_1_id_8.png -> извлекаем индекс 1
                const match = url.match(/main_(\d+)_id_\d+\./);
                if (match) {
                    const index = parseInt(match[1], 10);
                    if (index > maxIndex) {
                        maxIndex = index;
                    }
                }
            });
            
            // Следующий индекс = максимальный + 1, или 0 если изображений нет
            const startIndex = maxIndex + 1;
            
            console.log('Max existing index:', maxIndex);
            console.log('Starting new uploads from index:', startIndex);
            
            const uploadPromises = files.map(async (file, idx) => {
                const index = startIndex + idx;
                console.log(`Uploading file ${idx}: ${file.name} with index ${index}`);
                const url = await uploadGalleryImage(file, record.id, index);
                console.log(`Uploaded file ${idx} to: ${url}`);
                return url;
            });
            
            const uploadedUrls = await Promise.all(uploadPromises);
            
            console.log('All files uploaded:', uploadedUrls);
            
            // Добавляем каждое изображение через POST /animals/images
            const imageAddPromises = uploadedUrls.map(async (url, idx) => {
                console.log(`Adding image ${idx} to database: ${url}`);
                const result = await dataProvider.create('animals/images', {
                    data: {
                        animal_id: record.id,
                        url: url
                    }
                });
                console.log(`Image ${idx} added to database:`, result.data);
                // Возвращаем объект с id и url
                return result.data;
            });
            
            const addedImages = await Promise.all(imageAddPromises);
            
            const newImages = [...currentImages, ...addedImages];
            console.log('New images array:', newImages);
            setImages(newImages);
            
            notify(`Загружено ${files.length} изображений`, { type: 'success' });
        } catch (error) {
            console.error('Error uploading gallery images:', error);
            notify(`Ошибка загрузки: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };
    
    const handleDeleteImage = async (index) => {
        console.log('=== handleDeleteImage START ===');
        console.log('Deleting image at index:', index);
        
        if (!record || !record.id) {
            console.error('No record or record.id');
            notify('Ошибка: ID животного не найден', { type: 'error' });
            return;
        }
        
        const imageToDelete = images[index];
        console.log('Image to delete:', imageToDelete);
        
        if (!imageToDelete) {
            console.error('Image not found at index:', index);
            notify('Ошибка: изображение не найдено', { type: 'error' });
            return;
        }
        
        // Получаем ID изображения и URL (поддержка старого и нового формата)
        const imageId = typeof imageToDelete === 'object' ? imageToDelete.id : null;
        const imageUrl = typeof imageToDelete === 'object' ? imageToDelete.url : imageToDelete;
        
        console.log('Image ID:', imageId);
        console.log('Image URL:', imageUrl);
        
        if (!imageId) {
            console.error('No image ID found');
            notify('Ошибка: ID изображения не найден', { type: 'error' });
            return;
        }
        
        // Извлекаем индекс из URL файла (main_3_id_8.png -> индекс 3)
        const match = imageUrl.match(/main_(\d+)_id_\d+\./);
        const fileIndex = match ? parseInt(match[1], 10) : index;
        
        console.log('File index extracted from URL:', fileIndex);
        
        setUploading(true);
        
        try {
            console.log('Step 1: Deleting file from S3...');
            console.log('Calling deleteGalleryImage with animalId:', record.id, 'fileIndex:', fileIndex);
            
            // Удаляем файл из S3 используя индекс из имени файла
            await deleteGalleryImage(record.id, fileIndex);
            
            console.log('Step 1: File deleted from S3 successfully');
            console.log('Step 2: Deleting from database...');
            console.log('Calling dataProvider.delete with imageId:', imageId);
            
            // Удаляем из БД через API
            await dataProvider.delete('animals/images', {
                id: imageId,
                previousData: imageToDelete
            });
            
            console.log('Step 2: Deleted from database successfully');
            
            // Удаляем из локального массива
            const newImages = images.filter((_, i) => i !== index);
            console.log('New images array:', newImages);
            setImages(newImages);
            
            console.log('=== handleDeleteImage SUCCESS ===');
            notify('Изображение удалено', { type: 'success' });
        } catch (error) {
            console.error('=== handleDeleteImage ERROR ===');
            console.error('Error details:', error);
            console.error('Error message:', error.message);
            console.error('Error stack:', error.stack);
            notify(`Ошибка удаления: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };
    
    if (!record) {
        return <DetailLoadingState title="Загружаем информацию о животном..." />;
    }
    
    const mainImageUrl = getMainImageUrl({ images });
    const taxonomy = [
        record.family?.order?.class_?.name,
        record.family?.order?.name,
        record.family?.name
    ].filter(Boolean);

    return (
        <Box className="animal-editor-page">
            {uploading && <LinearProgress className="animal-editor-progress" aria-label="Загрузка файла" />}

            <Paper className="animal-editor-hero" elevation={0}>
                <Box className="animal-editor-hero-image">
                    {isValidImageUrl(mainImageUrl) ? (
                        <Box component="img" src={mainImageUrl} alt={record.name || 'Животное'} />
                    ) : (
                        <PetsIcon />
                    )}
                </Box>
                <Box className="animal-editor-hero-copy">
                    <Box className="animal-editor-hero-kicker">
                        <Typography component="span">Карточка животного</Typography>
                        <Chip size="small" label={`ID ${record.id}`} className="animal-editor-id-chip" />
                    </Box>
                    <Typography component="h1" className="animal-editor-title">
                        {record.name || 'Без названия'}
                    </Typography>
                    <Box className="animal-editor-taxonomy">
                        {taxonomy.length ? taxonomy.map((item, index) => (
                            <React.Fragment key={item}>
                                {index > 0 && <ChevronRightIcon aria-hidden="true" />}
                                <Typography component="span">{item}</Typography>
                            </React.Fragment>
                        )) : <Typography component="span">Классификация не указана</Typography>}
                    </Box>
                </Box>
                <Box className="animal-editor-hero-stats">
                    <Box>
                        <Typography component="strong">{images?.length || 0}</Typography>
                        <Typography component="span">фото</Typography>
                    </Box>
                    <Box>
                        <Typography component="strong">{record.parameters?.length || 0}</Typography>
                        <Typography component="span">параметра</Typography>
                    </Box>
                </Box>
            </Paper>

            <Box className="animal-editor-layout">
                <Box className="animal-editor-main-column">
                    <Paper className="animal-editor-section" elevation={0}>
                        <AnimalEditorSectionTitle
                            icon={<InfoOutlinedIcon />}
                            title="Основная информация"
                            description="Название и тексты, которые увидят посетители"
                        />
                        <Box className="animal-editor-fields">
                            <TextInput source="name" label="Название животного" fullWidth required />
                            <TextInput
                                source="description"
                                label="Краткое описание"
                                helperText="Главный текст карточки животного"
                                multiline
                                minRows={5}
                                fullWidth
                            />
                            <Box className="animal-editor-two-fields">
                                <TextInput
                                    source="environment_description"
                                    label="В естественной среде"
                                    helperText="Ареал, поведение и особенности жизни"
                                    multiline
                                    minRows={5}
                                    fullWidth
                                />
                                <TextInput
                                    source="zoo_description"
                                    label="В Новосибирском зоопарке"
                                    helperText="Где находится и как живёт у нас"
                                    multiline
                                    minRows={5}
                                    fullWidth
                                />
                            </Box>
                        </Box>
                    </Paper>

                    <Paper className="animal-editor-section" elevation={0}>
                        <AnimalEditorSectionTitle
                            icon={<LocationOnOutlinedIcon />}
                            title="Место на карте"
                            description="Введите координаты или укажите точку прямо на карте"
                        />
                        <Box className="animal-editor-coordinate-fields">
                            <TextInput source="geolocation[0]" label="Широта" fullWidth helperText={false} />
                            <TextInput source="geolocation[1]" label="Долгота" fullWidth helperText={false} />
                        </Box>
                        <Box className="animal-editor-map">
                            <FormDataConsumer>
                                {({ formData }) => {
                                    const handleCoordinatesChange = async (lat, lon) => {
                                        setValue('geolocation[0]', lat.toString(), { shouldDirty: true, shouldTouch: true });
                                        setValue('geolocation[1]', lon.toString(), { shouldDirty: true, shouldTouch: true });

                                        if (record && record.id) {
                                            try {
                                                await dataProvider.update('animals', {
                                                    id: record.id,
                                                    data: { ...record, geolocation: [lat, lon] },
                                                    previousData: record
                                                });
                                                notify('Координаты успешно обновлены', { type: 'success' });
                                            } catch (error) {
                                                console.error('Error updating coordinates:', error);
                                                notify(`Ошибка обновления координат: ${error.message}`, { type: 'error' });
                                            }
                                        }
                                    };

                                    return (
                                        <YandexMapPicker
                                            latitude={formData.geolocation?.[0] || '55.056551'}
                                            longitude={formData.geolocation?.[1] || '82.886962'}
                                            onCoordinatesChange={handleCoordinatesChange}
                                        />
                                    );
                                }}
                            </FormDataConsumer>
                        </Box>
                    </Paper>

                    <Paper className="animal-editor-section animal-editor-gallery-section" elevation={0}>
                        <AnimalEditorSectionTitle
                            icon={<PhotoLibraryOutlinedIcon />}
                            title="Фотогалерея"
                            description="Первое изображение используется как обложка карточки"
                            action={(
                                <Button
                                    variant="contained"
                                    component="label"
                                    startIcon={<AddIcon />}
                                    disabled={uploading}
                                    className="animal-editor-add-media"
                                >
                                    Добавить фото
                                    <input type="file" hidden accept="image/*" multiple onChange={handleGalleryImagesChange} disabled={uploading} />
                                </Button>
                            )}
                        />

                        {images && images.length > 0 ? (
                            <Box className="animal-editor-gallery-grid">
                                {images.map((image, index) => {
                                    const imageUrl = typeof image === 'string' ? image : image.url;
                                    const imageId = typeof image === 'object' ? image.id : null;
                                    return (
                                        <Box className={`animal-editor-gallery-card${index === 0 ? ' is-main' : ''}`} key={imageId || imageUrl || index}>
                                            <Box className="animal-editor-gallery-image">
                                                <Box component="img" src={imageUrl} alt={`${record.name || 'Животное'}, фото ${index + 1}`} loading="lazy" />
                                                {index === 0 && (
                                                    <Chip icon={<StarRoundedIcon />} label="Обложка" size="small" className="animal-editor-main-photo-chip" />
                                                )}
                                            </Box>
                                            <Box className="animal-editor-gallery-footer">
                                                <Typography noWrap>{index === 0 ? 'Основное фото' : `Фото ${index + 1}`}</Typography>
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteImage(index)}
                                                    disabled={uploading}
                                                    startIcon={<DeleteOutlineIcon />}
                                                >
                                                    Удалить
                                                </Button>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : (
                            <Box className="animal-editor-gallery-empty">
                                <PhotoLibraryOutlinedIcon />
                                <Typography component="strong">Фотографий пока нет</Typography>
                                <Typography>Добавьте обложку и фотографии для галереи</Typography>
                                <Button variant="outlined" component="label" startIcon={<CloudUploadOutlinedIcon />} disabled={uploading}>
                                    Загрузить фотографии
                                    <input type="file" hidden accept="image/*" multiple onChange={handleGalleryImagesChange} disabled={uploading} />
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Box>

                <Box className="animal-editor-side-column">
                    <Paper className="animal-editor-section" elevation={0}>
                        <AnimalEditorSectionTitle
                            icon={<TuneOutlinedIcon />}
                            title="Параметры"
                            description="Короткие факты для карточки"
                        />
                        <Box className="animal-editor-parameter-list">
                            {[0, 1, 2, 3].map((index) => (
                                <Box className="animal-editor-parameter-row" key={index}>
                                    <Typography className="animal-editor-parameter-number">{index + 1}</Typography>
                                    <TextInput source={`parameters[${index}].key`} label="Параметр" fullWidth helperText={false} />
                                    <TextInput source={`parameters[${index}].value`} label="Значение" fullWidth helperText={false} />
                                </Box>
                            ))}
                        </Box>
                    </Paper>

                    <Paper className="animal-editor-section animal-editor-media-section" elevation={0}>
                        <AnimalEditorSectionTitle
                            icon={<ImageOutlinedIcon />}
                            title="Медиа"
                            description="Иконки для интерфейса и аудиорассказ"
                        />

                        <Box className="animal-editor-media-card">
                            <Box className="animal-editor-media-card-header">
                                <Box>
                                    <Typography component="h3">Иконка на карте</Typography>
                                    <Typography>Отображается рядом с вольером</Typography>
                                </Box>
                                {!deleteMapIcon && isValidImageUrl(mapIconUrl) && (
                                    <Button size="small" color="error" onClick={handleDeleteMapIcon} disabled={uploading}>Удалить</Button>
                                )}
                            </Box>
                            {!deleteMapIcon && isValidImageUrl(mapIconUrl) ? (
                                <Box className="animal-editor-media-preview is-map">
                                    <Box component="img" src={mapIconUrl} alt="Иконка животного на карте" />
                                </Box>
                            ) : <AnimalMediaEmpty label="Иконка не загружена" />}
                            {mapIconFile && <Typography className="animal-editor-file-name">{mapIconFile.name}</Typography>}
                            <Button variant="outlined" component="label" fullWidth startIcon={<CloudUploadOutlinedIcon />} disabled={uploading}>
                                {isValidImageUrl(mapIconUrl) && !deleteMapIcon ? 'Заменить иконку' : 'Загрузить иконку'}
                                <input type="file" hidden accept="image/*" onChange={handleMapIconChange} disabled={uploading} />
                            </Button>
                        </Box>

                        <Box className="animal-editor-media-card">
                            <Box className="animal-editor-media-card-header">
                                <Box>
                                    <Typography component="h3">Иконка в списке</Typography>
                                    <Typography>Компактное изображение для каталога</Typography>
                                </Box>
                                {!shouldDeleteListIconFlag && isValidImageUrl(listIconUrl) && (
                                    <Button size="small" color="error" onClick={handleDeleteListIcon} disabled={uploading}>Удалить</Button>
                                )}
                            </Box>
                            {!shouldDeleteListIconFlag && isValidImageUrl(listIconUrl) ? (
                                <Box className="animal-editor-media-preview">
                                    <Box component="img" src={listIconUrl} alt="Иконка животного в списке" />
                                </Box>
                            ) : <AnimalMediaEmpty label="Иконка не загружена" />}
                            {listIconFile && <Typography className="animal-editor-file-name">{listIconFile.name}</Typography>}
                            <Button variant="outlined" component="label" fullWidth startIcon={<CloudUploadOutlinedIcon />} disabled={uploading}>
                                {isValidImageUrl(listIconUrl) && !shouldDeleteListIconFlag ? 'Заменить иконку' : 'Загрузить иконку'}
                                <input type="file" hidden accept="image/*" onChange={handleListIconChange} disabled={uploading} />
                            </Button>
                        </Box>

                        <Box className="animal-editor-media-card">
                            <Box className="animal-editor-media-card-header">
                                <Box>
                                    <Typography component="h3">Аудиорассказ</Typography>
                                    <Typography>MP3, WAV, OGG, M4A, AAC или FLAC</Typography>
                                </Box>
                                {!shouldDeleteAudioFlag && isValidImageUrl(audioUrl) && (
                                    <Button size="small" color="error" onClick={handleDeleteAudio} disabled={uploading}>Удалить</Button>
                                )}
                            </Box>
                            {!shouldDeleteAudioFlag && isValidImageUrl(audioUrl) ? (
                                <Box className="animal-editor-audio-player">
                                    <VolumeUpOutlinedIcon aria-hidden="true" />
                                    <audio controls src={audioUrl}>Ваш браузер не поддерживает аудио</audio>
                                </Box>
                            ) : (
                                <Box className="animal-editor-media-empty is-audio">
                                    <VolumeUpOutlinedIcon />
                                    <Typography>Аудио не загружено</Typography>
                                </Box>
                            )}
                            {audioFile && <Typography className="animal-editor-file-name">{audioFile.name}</Typography>}
                            <Button variant="outlined" component="label" fullWidth startIcon={<CloudUploadOutlinedIcon />} disabled={uploading}>
                                {isValidImageUrl(audioUrl) && !shouldDeleteAudioFlag ? 'Заменить аудио' : 'Загрузить аудио'}
                                <input
                                    type="file"
                                    hidden
                                    accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/ogg,audio/mp4,audio/x-m4a,audio/aac,audio/flac,.mp3,.wav,.ogg,.m4a,.aac,.flac"
                                    onChange={handleAudioChange}
                                    disabled={uploading}
                                />
                            </Button>
                        </Box>
                    </Paper>

                    <Paper className="animal-editor-section" elevation={0}>
                        <AnimalEditorSectionTitle
                            icon={<LinkOutlinedIcon />}
                            title="Ссылка для QR-кода"
                            description="Откроется после сканирования таблички"
                        />
                        <TextInput source="qr_url" label="URL страницы" fullWidth helperText="Например: https://zoonovosib.ru/animals/..." />
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};

// Компонент для создания отряда
const CreateOrderDialog = ({ open, onClose }) => {
    const [selectedClass, setSelectedClass] = useState('');
    const [orderName, setOrderName] = useState('');
    const [classes, setClasses] = useState([]);
    
    const dataProvider = useDataProvider();
    const notify = useNotify();

    // Загружаем классы при открытии диалога
    useEffect(() => {
        if (open) {
            dataProvider.getList('classes', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            }).then(({ data }) => {
                setClasses(data);
            }).catch(error => {
                console.error('Error loading classes:', error);
                notify('Ошибка загрузки классов', { type: 'error' });
            });
        }
    }, [open, dataProvider, notify]);

    const handleCreate = async () => {
        if (!orderName || !selectedClass) {
            notify('Название и класс обязательны', { type: 'warning' });
            return;
        }

        try {
            await dataProvider.create('orders', {
                data: {
                    name: orderName,
                    class_id: selectedClass
                }
            });
            
            notify('Отряд успешно создан', { type: 'success' });
            handleClose();
        } catch (error) {
            console.error('Error creating order:', error);
            notify(`Ошибка при создании: ${error.message}`, { type: 'error' });
        }
    };

    const handleClose = () => {
        setSelectedClass('');
        setOrderName('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Создать отряд</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>Класс</InputLabel>
                        <Select
                            value={selectedClass}
                            onChange={(e) => setSelectedClass(e.target.value)}
                            label="Класс"
                        >
                            {classes.map((cls) => (
                                <MenuItem key={cls.id} value={cls.id}>
                                    {cls.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <MuiTextField
                        label="Название отряда *"
                        value={orderName}
                        onChange={(e) => setOrderName(e.target.value)}
                        fullWidth
                        required
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Отмена</Button>
                <Button onClick={handleCreate} variant="contained" disabled={!orderName || !selectedClass}>
                    Создать
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Компонент для создания семейства
const CreateFamilyDialog = ({ open, onClose }) => {
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedOrder, setSelectedOrder] = useState('');
    const [familyName, setFamilyName] = useState('');
    const [classes, setClasses] = useState([]);
    const [orders, setOrders] = useState([]);
    
    const dataProvider = useDataProvider();
    const notify = useNotify();

    // Загружаем классы при открытии диалога
    useEffect(() => {
        if (open) {
            dataProvider.getList('classes', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            }).then(({ data }) => {
                setClasses(data);
            }).catch(error => {
                console.error('Error loading classes:', error);
                notify('Ошибка загрузки классов', { type: 'error' });
            });
        }
    }, [open, dataProvider, notify]);

    // Загружаем отряды при выборе класса
    useEffect(() => {
        if (selectedClass) {
            dataProvider.getList('orders', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            }).then(({ data }) => {
                const filteredOrders = data.filter(order => order.class_?.id === selectedClass);
                setOrders(filteredOrders);
            }).catch(error => {
                console.error('Error loading orders:', error);
                notify('Ошибка загрузки отрядов', { type: 'error' });
            });
        } else {
            setOrders([]);
            setSelectedOrder('');
        }
    }, [selectedClass, dataProvider, notify]);

    const handleCreate = async () => {
        if (!familyName || !selectedOrder) {
            notify('Название и отряд обязательны', { type: 'warning' });
            return;
        }

        try {
            await dataProvider.create('families', {
                data: {
                    name: familyName,
                    order_id: selectedOrder
                }
            });
            
            notify('Семейство успешно создано', { type: 'success' });
            handleClose();
        } catch (error) {
            console.error('Error creating family:', error);
            notify(`Ошибка при создании: ${error.message}`, { type: 'error' });
        }
    };

    const handleClose = () => {
        setSelectedClass('');
        setSelectedOrder('');
        setFamilyName('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Создать семейство</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>Класс</InputLabel>
                        <Select
                            value={selectedClass}
                            onChange={(e) => setSelectedClass(e.target.value)}
                            label="Класс"
                        >
                            {classes.map((cls) => (
                                <MenuItem key={cls.id} value={cls.id}>
                                    {cls.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth disabled={!selectedClass}>
                        <InputLabel>Отряд</InputLabel>
                        <Select
                            value={selectedOrder}
                            onChange={(e) => setSelectedOrder(e.target.value)}
                            label="Отряд"
                        >
                            {orders.map((order) => (
                                <MenuItem key={order.id} value={order.id}>
                                    {order.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <MuiTextField
                        label="Название семейства *"
                        value={familyName}
                        onChange={(e) => setFamilyName(e.target.value)}
                        fullWidth
                        required
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Отмена</Button>
                <Button onClick={handleCreate} variant="contained" disabled={!familyName || !selectedOrder}>
                    Создать
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Компонент для создания животного
const CreateAnimalDialog = ({ open, onClose }) => {
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedOrder, setSelectedOrder] = useState('');
    const [selectedFamily, setSelectedFamily] = useState('');
    const [classes, setClasses] = useState([]);
    const [orders, setOrders] = useState([]);
    const [families, setFamilies] = useState([]);
    const [animalData, setAnimalData] = useState({
        name: '',
        description: '',
        environment_description: '',
        zoo_description: '',
        geolocation: ['55.0552000000', '82.8790460000'],
        qr_url: 'http://example.com',
        map_icon_url: 'http://example.com',
        list_icon_url: 'http://example.com',
        audio_url: 'http://example.com'
    });
    
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();

    // Загружаем классы при открытии диалога
    useEffect(() => {
        if (open) {
            dataProvider.getList('classes', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            }).then(({ data }) => {
                setClasses(data);
            }).catch(error => {
                console.error('Error loading classes:', error);
                notify('Ошибка загрузки классов', { type: 'error' });
            });
        }
    }, [open, dataProvider, notify]);

    // Загружаем отряды при выборе класса
    useEffect(() => {
        if (selectedClass) {
            dataProvider.getList('orders', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            }).then(({ data }) => {
                // Фильтруем отряды по выбранному классу
                const filteredOrders = data.filter(order => order.class_?.id === selectedClass);
                setOrders(filteredOrders);
            }).catch(error => {
                console.error('Error loading orders:', error);
                notify('Ошибка загрузки отрядов', { type: 'error' });
            });
        } else {
            setOrders([]);
            setSelectedOrder('');
        }
    }, [selectedClass, dataProvider, notify]);

    // Загружаем семейства при выборе отряда
    useEffect(() => {
        if (selectedOrder) {
            dataProvider.getList('families', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            }).then(({ data }) => {
                // Фильтруем семейства по выбранному отряду
                const filteredFamilies = data.filter(family => family.order?.id === selectedOrder);
                setFamilies(filteredFamilies);
            }).catch(error => {
                console.error('Error loading families:', error);
                notify('Ошибка загрузки семейств', { type: 'error' });
            });
        } else {
            setFamilies([]);
            setSelectedFamily('');
        }
    }, [selectedOrder, dataProvider, notify]);

    const handleCreate = async () => {
        if (!animalData.name || !selectedFamily) {
            notify('Название и семейство обязательны', { type: 'warning' });
            return;
        }

        try {
            const result = await dataProvider.create('animals', {
                data: {
                    ...animalData,
                    family_id: selectedFamily
                }
            });
            
            notify('Животное успешно создано', { type: 'success' });
            onClose();
            redirect('edit', 'animals', result.data.id);
        } catch (error) {
            console.error('Error creating animal:', error);
            notify(`Ошибка при создании: ${error.message}`, { type: 'error' });
        }
    };

    const handleClose = () => {
        setSelectedClass('');
        setSelectedOrder('');
        setSelectedFamily('');
        setAnimalData({
            name: '',
            description: '',
            environment_description: '',
            zoo_description: '',
            geolocation: ['55.0552000000', '82.8790460000'],
            qr_url: 'http://example.com',
            map_icon_url: 'http://example.com',
            list_icon_url: 'http://example.com',
            audio_url: 'http://example.com'
        });
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Создать животное</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    {/* Выбор класса */}
                    <FormControl fullWidth>
                        <InputLabel>Класс</InputLabel>
                        <Select
                            value={selectedClass}
                            onChange={(e) => setSelectedClass(e.target.value)}
                            label="Класс"
                        >
                            {classes.map((cls) => (
                                <MenuItem key={cls.id} value={cls.id}>
                                    {cls.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Выбор отряда */}
                    <FormControl fullWidth disabled={!selectedClass}>
                        <InputLabel>Отряд</InputLabel>
                        <Select
                            value={selectedOrder}
                            onChange={(e) => setSelectedOrder(e.target.value)}
                            label="Отряд"
                        >
                            {orders.map((order) => (
                                <MenuItem key={order.id} value={order.id}>
                                    {order.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Выбор семейства */}
                    <FormControl fullWidth disabled={!selectedOrder}>
                        <InputLabel>Семейство</InputLabel>
                        <Select
                            value={selectedFamily}
                            onChange={(e) => setSelectedFamily(e.target.value)}
                            label="Семейство"
                        >
                            {families.map((family) => (
                                <MenuItem key={family.id} value={family.id}>
                                    {family.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Поля для ввода данных животного */}
                    <MuiTextField
                        label="Название *"
                        value={animalData.name}
                        onChange={(e) => setAnimalData({ ...animalData, name: e.target.value })}
                        fullWidth
                        required
                    />

                    <MuiTextField
                        label="Описание"
                        value={animalData.description}
                        onChange={(e) => setAnimalData({ ...animalData, description: e.target.value })}
                        fullWidth
                        multiline
                        rows={4}
                    />

                    <MuiTextField
                        label="Описание среды обитания"
                        value={animalData.environment_description}
                        onChange={(e) => setAnimalData({ ...animalData, environment_description: e.target.value })}
                        fullWidth
                        multiline
                        rows={3}
                    />

                    <MuiTextField
                        label="Описание в зоопарке"
                        value={animalData.zoo_description}
                        onChange={(e) => setAnimalData({ ...animalData, zoo_description: e.target.value })}
                        fullWidth
                        multiline
                        rows={3}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Отмена</Button>
                <Button onClick={handleCreate} variant="contained" disabled={!animalData.name || !selectedFamily}>
                    Создать
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Кастомные actions для списка с кнопками создания
const AnimalListHeader = () => {
    const [createAnimalDialogOpen, setCreateAnimalDialogOpen] = useState(false);
    const [createOrderDialogOpen, setCreateOrderDialogOpen] = useState(false);
    const [createFamilyDialogOpen, setCreateFamilyDialogOpen] = useState(false);
    const { total = 0 } = useListContext();

    return (
        <>
            <Box className="animals-page-header">
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="h4">База животных</Typography>
                        <Chip size="small" label={`${total} записей`} className="animals-total-chip" />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Каталог животных, классификация и материалы для приложения
                    </Typography>
                </Box>
                <Box className="animals-page-actions">
                    <Button
                        variant="outlined"
                        onClick={() => setCreateOrderDialogOpen(true)}
                        size="small"
                    >
                        Создать отряд
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setCreateFamilyDialogOpen(true)}
                        size="small"
                    >
                        Создать семейство
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateAnimalDialogOpen(true)}
                        sx={{ fontWeight: 600 }}
                    >
                        Создать животное
                    </Button>
                </Box>
            </Box>
            <CreateOrderDialog 
                open={createOrderDialogOpen} 
                onClose={() => setCreateOrderDialogOpen(false)} 
            />
            <CreateFamilyDialog 
                open={createFamilyDialogOpen} 
                onClose={() => setCreateFamilyDialogOpen(false)} 
            />
            <CreateAnimalDialog 
                open={createAnimalDialogOpen} 
                onClose={() => setCreateAnimalDialogOpen(false)} 
            />
        </>
    );
};

const AnimalListContent = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box className="animals-list-page">
            <AnimalListHeader />
            <AnimalFilters />
            <Paper className="animals-table-surface" elevation={0}>
                {isMobile ? <AnimalMobileList /> : <AnimalDatagrid />}
            </Paper>
        </Box>
    );
};

export const AnimalList = (props) => (
    <List 
        {...props}
        exporter={false}
        title="Животные"
        perPage={30}
        sort={{ field: 'id', order: 'ASC' }}
        actions={false}
        empty={false}
        sx={{
            '& .RaList-main': { pt: 0 },
            '& .RaList-content': { boxShadow: 'none', border: 0, background: 'transparent', overflow: 'visible' },
        }}
    >
        <AnimalListContent />
    </List>
);
